import React, { useContext } from 'react';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { ArrowRightIcon, CameraFrameIcon, IconText, InspectIcon, LockClosedIcon, SunIcon } from '@motorway/mw-highway-code';

import { DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS } from 'Utilities/analytics/analyticsDocumentCapture';
import { EVENT } from 'Utilities/analytics/optimizely/constants';
import { useOptimizelyTrack } from 'Utilities/analytics/optimizely/useOptimizelyTrack';

import { ComponentContent, CONTENT_SIZES } from 'Layout';

import { TipProps } from 'Storybook/components/local/RecommendedPhotoTips/RecommendedPhotoTips.types';

import ContactSalesTeam from '../../../../../components/misc/Agent/ContactSalesTeam';
import { ContactSalesTeamProps } from '../../../../../components/misc/Agent/ContactSalesTeam.types';
import { RecommendedPhotoTips } from '../../../../../storybook/components/local/RecommendedPhotoTips/RecommendedPhotoTips';
import { PlateAndCollectionSummary } from '../../DealerDetails/PlateAndCollectionSummary/PlateAndCollectionSummary';
import { DealerVerificationContainer } from '../../DealerVerificationContainer/DealerVerificationContainer';
import { DOCUMENT_KIND_FINANCIAL_LETTER, DOCUMENT_KINDS } from '../../DocumentCapture.helpers';
import { FinanceSettlementProviderContext } from '../../DocumentCaptureContexts';
import {
  OurTrustedProviderModal,
} from '../../OurTrustedProviderModal/OurTrustedProviderModal';
import DocumentCaptureProgressStepper from '../DocumentCaptureProgressStepper/DocumentCaptureProgressStepper';

import LocalTexts from './DocumentCaptureViewContainer.texts.json';

import styles from '../../DocumentCaptureView/DocumentCaptureView.scss';

const LINK_TO_HOW_SECURE_IS_MY_PERSONAL_INFORMATION = 'https://help.motorway.co.uk/hc/en-gb/articles/6894817782428-How-secure-is-my-personal-information-';

const LocalT = new MDText(LocalTexts.documentCapture);

type DocumentCaptureViewContainerProps = {
  children: React.ReactNode;
  contactInfo?: Partial<ContactSalesTeamProps>;
  index?: number;
  isEarlyDocsFlow?: boolean;
  isSummaryPage?: boolean;
  kind?: string;
  showDealerVerification?: boolean;
  showDocumentInformation?: boolean;
  showSaleDetailsOnRHS?: boolean;
  stepper?: boolean;
};

export const tips: TipProps[] = [
  {
    icon: CameraFrameIcon,
    recommendation: LocalT.translate('tips.recommendation1') as string,
    recommendationTitle: LocalT.translate('tips.recommendationTitle1') as string,
  },
  {
    icon: SunIcon,
    recommendation: LocalT.translate('tips.recommendation2') as string,
    recommendationTitle: LocalT.translate('tips.recommendationTitle2') as string,
  },
  {
    icon: InspectIcon,
    recommendation: LocalT.translate('tips.recommendation3') as string,
    recommendationTitle: LocalT.translate('tips.recommendationTitle3') as string,
  },
];

const handleHyperlinkClick = (documentType?: string) => {
  DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS.DOCUMENTS_UPLOAD_PRIVACY_INFORMATION_LINK_CLICKED(documentType);
};

// eslint-disable-next-line max-len
export const DocumentCaptureViewContainer = ({
  children,
  contactInfo = {},
  index = 0,
  isEarlyDocsFlow = false,
  isSummaryPage = false,
  kind,
  showDealerVerification = false,
  showDocumentInformation = false,
  showSaleDetailsOnRHS = false,
  stepper = false,
}: DocumentCaptureViewContainerProps) => {
  const { email, phoneNumber } = contactInfo;
  const contactEmail = email ?? LocalT.translate('docCaptureSupportEmail');
  const contactPhone = phoneNumber ?? LocalT.translate('docCaptureSupportPhoneNumber');
  const privacyTitle = LocalT.translate('privacy.title');
  const privacyLinkTitle = LocalT.translate('privacy.link');

  const docIsFinanceLetter = kind === DOCUMENT_KINDS.FINANCE_SETTLEMENT_LETTER
   || kind === DOCUMENT_KIND_FINANCIAL_LETTER;

  const financePageEnabled = docIsFinanceLetter;
  const { financeAgreement, isAutomatedDocumentUpload } = useContext(FinanceSettlementProviderContext);

  const { automatedFinanceProvider, failedLookupReason, optedOut } = financeAgreement || {};

  const showAutomatedFslFlow = (financePageEnabled && automatedFinanceProvider && !optedOut && !failedLookupReason);
  const showAutomatedDocUploadFlow = (isAutomatedDocumentUpload && isSummaryPage) || isEarlyDocsFlow;

  const { OPTIMIZELY_TRACK } = useOptimizelyTrack();
  const trackContactCTAClick = () => {
    OPTIMIZELY_TRACK(EVENT.AGENT_SUPPORT_CONTACTED, { forceDpSellerId: true });
  };

  return (
    <>
      <ComponentContent
        className={styles.contentBox}
        maxWidth={CONTENT_SIZES.large}
        paddings={false}
        selector="article"
      >
        {stepper ? <DocumentCaptureProgressStepper index={index} /> : null}
        {children}
      </ComponentContent>
      <ComponentContent
        className={cx(styles.contentBox, styles.aside)}
        maxWidth={CONTENT_SIZES.narrow}
        paddings={false}
        selector="aside"
      >
        <div className={styles.stickyBase}>
          {showDealerVerification && <DealerVerificationContainer />}

          {showDocumentInformation && (
            <>
              {!showAutomatedFslFlow
                && <div className={styles.recommendedPhotoTips}>
                  <RecommendedPhotoTips showBorder tips={tips}/>
                </div>
              }
              <section className={styles.privacyInfoBox}>
                <h5 className={styles.title}>{privacyTitle}</h5>
                <a href={LINK_TO_HOW_SECURE_IS_MY_PERSONAL_INFORMATION} onClick={() => handleHyperlinkClick(kind)} target="_blank">
                  <div className={styles.content}>
                    <IconText icon={LockClosedIcon} text={privacyLinkTitle} variant="small--heading6" />
                    <span className={styles.arrowIcon}><ArrowRightIcon size="xsmall" /></span>
                  </div>
                </a>
              </section>
            </>
          )}

          {showSaleDetailsOnRHS
            ? <span className={styles.collectionSummary}><PlateAndCollectionSummary /></span>
            : null
          }

          <ContactSalesTeam
            className={styles.contactSalesTeam}
            email={contactEmail}
            onCTAClick={trackContactCTAClick}
            phoneNumber={contactPhone as string}
          />

          {showAutomatedDocUploadFlow && (
            <OurTrustedProviderModal />
          )}
        </div>
      </ComponentContent>
    </>
  );
};
